import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Card,
    CardContent,
    Button,
    Chip,
    Divider,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText
} from '@mui/material'
import {
    ArrowBack as BackIcon,
    LocationOn as LocationIcon,
    Phone as PhoneIcon,
    CheckCircle as DeliveredIcon,
    LocalShipping as ShippingIcon
} from '@mui/icons-material'
import { doc, onSnapshot, updateDoc, Timestamp } from 'firebase/firestore'
import { getFirebaseDb } from '../../config/firebase'

import { formatCurrency, getOrderStatusColor } from '../../utils'
import type { Order, OrderStatus } from '../../types'
import toast from 'react-hot-toast'

export default function DeliveryDetailPage() {
    const { orderId } = useParams()
    const [order, setOrder] = useState<Order | null>(null)
    const [loading, setLoading] = useState(true)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [updating, setUpdating] = useState(false)


    const navigate = useNavigate()
    const db = getFirebaseDb()

    useEffect(() => {
        if (!orderId) return

        const unsubscribe = onSnapshot(doc(db, 'orders', orderId), (snapshot) => {
            if (snapshot.exists()) {
                setOrder({ id: snapshot.id, ...snapshot.data() } as Order)
            }
            setLoading(false)
        })

        return () => unsubscribe()
    }, [db, orderId])

    const handleUpdateStatus = async (newStatus: OrderStatus) => {
        if (!orderId) return
        setUpdating(true)

        try {
            await updateDoc(doc(db, 'orders', orderId), {
                status: newStatus,
                updatedAt: Timestamp.now(),
                ...(newStatus === 'delivered' && { 'delivery.actualDate': Timestamp.now() })
            })
            toast.success(`Status updated to ${newStatus}`)
            setDialogOpen(false)

            if (newStatus === 'delivered') {
                navigate('/')
            }
        } catch (error) {
            toast.error('Failed to update status')
        } finally {
            setUpdating(false)
        }
    }

    const getNextStatus = (): OrderStatus | null => {
        if (!order) return null
        const statusFlow: Record<string, OrderStatus> = {
            'confirmed': 'processing',
            'processing': 'shipped',
            'shipped': 'delivered'
        }
        return statusFlow[order.status] || null
    }

    const getActionButton = () => {
        const nextStatus = getNextStatus()
        if (!nextStatus) return null

        const buttonConfig: Record<OrderStatus, { label: string; icon: React.ReactNode; color: string }> = {
            'processing': { label: 'Pick Up Order', icon: <ShippingIcon />, color: '#f59e0b' },
            'shipped': { label: 'Start Delivery', icon: <ShippingIcon />, color: '#3b82f6' },
            'delivered': { label: 'Mark as Delivered', icon: <DeliveredIcon />, color: '#10b981' },
            'pending': { label: '', icon: null, color: '' },
            'confirmed': { label: '', icon: null, color: '' },
            'cancelled': { label: '', icon: null, color: '' }
        }

        const config = buttonConfig[nextStatus]
        if (!config.label) return null

        return (
            <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={config.icon}
                onClick={() => setDialogOpen(true)}
                sx={{
                    py: 2,
                    fontSize: '1.1rem',
                    backgroundColor: config.color,
                    '&:hover': { backgroundColor: config.color, filter: 'brightness(0.9)' }
                }}
            >
                {config.label}
            </Button>
        )
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        )
    }

    if (!order) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography>Order not found</Typography>
                <Button onClick={() => navigate('/')}>Go Back</Button>
            </Box>
        )
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', pb: 12 }}>
            {/* App Bar */}
            <AppBar
                position="sticky"
                elevation={0}
                sx={{ backgroundColor: 'white', color: 'text.primary', borderBottom: '1px solid #e2e8f0' }}
            >
                <Toolbar>
                    <IconButton edge="start" onClick={() => navigate('/')} sx={{ mr: 2 }}>
                        <BackIcon />
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Order #{order.invoiceNumber || order.id.slice(0, 8)}
                        </Typography>
                    </Box>
                    <Chip
                        label={order.status}
                        color={getOrderStatusColor(order.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                    />
                </Toolbar>
            </AppBar>

            {/* Customer Info */}
            <Box sx={{ p: 2 }}>
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="subtitle2" sx={{ color: 'grey.500', mb: 2 }}>
                            Customer
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            {order.guestInfo?.name || 'Guest'}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <PhoneIcon sx={{ fontSize: 18, color: 'grey.500' }} />
                            <Typography variant="body2">{order.guestInfo?.phone || 'N/A'}</Typography>
                            <Button
                                size="small"
                                href={`tel:${order.guestInfo?.phone}`}
                                sx={{ ml: 'auto' }}
                            >
                                Call
                            </Button>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" sx={{ color: 'grey.500', mb: 1 }}>
                            Delivery Address
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <LocationIcon sx={{ fontSize: 20, color: 'primary.main', mt: 0.3 }} />
                            <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {order.deliveryAddress?.street}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'grey.600' }}>
                                    {order.deliveryAddress?.city}, {order.deliveryAddress?.province}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Order Items */}
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="subtitle2" sx={{ color: 'grey.500', mb: 2 }}>
                            Order Items ({order.items?.length || 0})
                        </Typography>
                        <List disablePadding>
                            {order.items?.map((item, index) => (
                                <ListItem key={index} disablePadding sx={{ py: 1 }}>
                                    <ListItemText
                                        primary={item.productName}
                                        secondary={`Qty: ${item.quantity}`}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(item.totalPrice)}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Total
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                {formatCurrency(order.pricing?.total || 0)}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'grey.500', mt: 1 }}>
                            Payment: {order.payment?.method?.toUpperCase() || 'COD'}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Fixed Action Button */}
            {getActionButton() && (
                <Box sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 2,
                    backgroundColor: 'white',
                    borderTop: '1px solid #e2e8f0',
                    boxShadow: '0 -4px 20px rgba(0,0,0,0.1)'
                }}>
                    {getActionButton()}
                </Box>
            )}

            {/* Confirmation Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>Confirm Status Update</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to update this order to "{getNextStatus()}"?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={updating}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => getNextStatus() && handleUpdateStatus(getNextStatus()!)}
                        disabled={updating}
                    >
                        {updating ? <CircularProgress size={24} /> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
